// Pure per-rider (N-of-M) waiver slot model. The ONE place that decides which
// rider is signing, whether the picker shows, and when the set is complete.
// Extracted so the three signing surfaces (PDP daily, PDP session, durable
// token page) cannot drift, and so it is unit-testable — vitest collects only
// *.test.ts, never .tsx.

export type WaiverMode = 'daily' | 'session'

/**
 * Sessions bind every signature to a CHOSEN rider slot; the sign RPCs reject a
 * NULL slot for a session with 'slot_required'. A solo session has exactly one
 * choice and renders no picker, so its slot MUST be pre-selected — otherwise
 * the pad renders with activeSlot === null and the sign handler bails before
 * calling the server (a silent no-op after the customer has already paid).
 * Daily bookings never pass a slot; the server fills them in signing order.
 */
export function initialActiveSlot(mode: WaiverMode, requiredRiders: number): number | null {
  return mode === 'session' && requiredRiders === 1 ? 1 : null
}

export type WaiverSlotView = {
  allSigned: boolean
  showPicker: boolean
  showCanvas: boolean
  /** Slot sent to the server: sessions bind explicitly, daily sends null. */
  slotForSign: number | null
  /** Slot recorded locally once the server accepts (daily mirrors `signed + 1`). */
  slotToRecord: number
}

export function waiverSlotView(input: {
  mode: WaiverMode
  requiredRiders: number
  signedSlots: number[]
  activeSlot: number | null
}): WaiverSlotView {
  const { mode, requiredRiders, signedSlots, activeSlot } = input
  const isSession = mode === 'session'
  const allSigned = signedSlots.length >= requiredRiders
  return {
    allSigned,
    showPicker: isSession && requiredRiders > 1 && !allSigned,
    showCanvas: !allSigned && (!isSession || activeSlot != null),
    slotForSign: isSession ? activeSlot : null,
    slotToRecord: isSession && activeSlot != null ? activeSlot : signedSlots.length + 1,
  }
}
